import { useState, useEffect } from "react";
import Card from "components/card";
import Manager from "./components/Manager";
import { MdAutoAwesome, MdCloudUpload, MdDelete, MdImage, MdVideoLibrary, MdDescription, MdLink } from "react-icons/md";
import { getCachedAvailableLatteImages, getRandomLatteImage } from "utils/imageUtils";

export interface LearningCard {
  id?: string;
  type: "Quiz" | "Tip" | "Link";
  title: string;
  content: string;
  assets: Asset[];
  status: "draft" | "published";
  createdAt?: string;
  updatedAt?: string;
}

export interface Asset {
  id: string;
  type: "image" | "document" | "video" | "link";
  name: string;
  url: string;
  size?: string;
  description?: string;
}

const ContentCreation = () => {
  const [savedCards, setSavedCards] = useState<LearningCard[]>([
    {
      id: "day1_00001_quiz",
      type: "Quiz",
      title: "Communication Quiz",
      content: "What is active listening?\n\na) Speaking clearly\nb) Focusing on the speaker\n\nAnswer: b",
      assets: [{
        id: "asset_quiz_1",
        type: "image",
        name: "Communication Quiz Image",
        url: "/assets/img/latte/1.png"
      }],
      status: "published",
      createdAt: "2025-01-05T10:30:00.000Z",
      updatedAt: "2025-01-06T14:20:00.000Z"
    },
    {
      id: "day1_00002_tip",
      type: "Tip",
      title: "Building Trust",
      content: "Be reliable and consistent.\nListen without judgment.\nKeep confidential information private.",
      assets: [{
        id: "asset_tip_2",
        type: "image",
        name: "Building Trust Image",
        url: "/assets/img/latte/2.png"
      }],
      status: "published",
      createdAt: "2025-01-04T09:15:00.000Z",
      updatedAt: "2025-01-07T11:45:00.000Z"
    },
    {
      id: "day1_00003_link",
      type: "Link",
      title: "Goal Setting Resources",
      content: "[SMART Goals Guide](https://example.com/smart-goals)",
      assets: [{
        id: "asset_link_3",
        type: "image",
        name: "Goal Setting Image",
        url: "/assets/img/latte/3.png"
      }],
      status: "draft",
      createdAt: "2025-01-03T16:20:00.000Z",
      updatedAt: "2025-01-03T16:20:00.000Z"
    },
    {
      id: "day1_00004_quiz",
      type: "Quiz",
      title: "Leadership Quiz",
      content: "What makes a good leader?\n\na) Making all decisions alone\nb) Balancing guidance with autonomy\n\nAnswer: b",
      assets: [{
        id: "asset_quiz_4",
        type: "image",
        name: "Leadership Quiz Image",
        url: "/assets/img/latte/4.png"
      }],
      status: "published",
      createdAt: "2025-01-02T13:10:00.000Z",
      updatedAt: "2025-01-05T16:30:00.000Z"
    },
    {
      id: "day1_00005_tip",
      type: "Tip",
      title: "Time Management",
      content: "Prioritize important tasks first.\nBlock time for focused work.\nTake regular breaks.",
      assets: [{
        id: "asset_tip_5",
        type: "image",
        name: "Time Management Image",
        url: "/assets/img/latte/5.png"
      }],
      status: "draft",
      createdAt: "2025-01-01T08:45:00.000Z",
      updatedAt: "2025-01-04T12:15:00.000Z"
    },
    {
      id: "day1_00006_link",
      type: "Link",
      title: "Career Resources",
      content: "[LinkedIn Guide](https://example.com/linkedin)",
      assets: [{
        id: "asset_link_6",
        type: "image",
        name: "Career Resources Image",
        url: "/assets/img/latte/6.png"
      }],
      status: "published",
      createdAt: "2024-12-28T14:30:00.000Z",
      updatedAt: "2025-01-08T09:20:00.000Z"
    }
  ]);
  const [isGeneratingBulk, setIsGeneratingBulk] = useState(false);
  const [generationCount, setGenerationCount] = useState(60);
  const [generationDescription, setGenerationDescription] = useState("");
  const [uploadedAssets, setUploadedAssets] = useState<Asset[]>([]);
  const [availableImages, setAvailableImages] = useState<number[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  // Load available images on component mount
  useEffect(() => {
    const loadAvailableImages = async () => {
      try {
        setIsLoadingImages(true);
        const images = await getCachedAvailableLatteImages();
        setAvailableImages(images);
      } catch (error) {
        console.error('Failed to load available images:', error);
        // Fallback to a basic set if loading fails
        setAvailableImages([1, 2, 3, 4, 5, 6]);
      } finally {
        setIsLoadingImages(false);
      }
    };
    
    loadAvailableImages();
  }, []);

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    
    const newAssets: Asset[] = Array.from(files).map((file, index) => ({
      id: `asset_${Date.now()}_${index}`,
      type: getAssetType(file.type),
      name: file.name,
      url: URL.createObjectURL(file),
      size: formatFileSize(file.size),
      description: ""
    }));
    
    setUploadedAssets(prev => [...prev, ...newAssets]);
  };

  const getAssetType = (fileType: string): Asset["type"] => {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.startsWith('video/')) return 'video';
    if (fileType.includes('pdf') || fileType.includes('document')) return 'document';
    return 'document';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeAsset = (assetId: string) => {
    setUploadedAssets(prev => prev.filter(asset => asset.id !== assetId));
  };

  const getAssetIcon = (type: Asset["type"]) => {
    switch (type) {
      case "image":
        return <MdImage className="h-4 w-4 text-green-500" />;
      case "video":
        return <MdVideoLibrary className="h-4 w-4 text-red-500" />;
      case "document":
        return <MdDescription className="h-4 w-4 text-blue-500" />;
      case "link":
        return <MdLink className="h-4 w-4 text-purple-500" />;
      default:
        return <MdDescription className="h-4 w-4 text-gray-500" />;
    }
  };

  const generateBulkCards = (count: number = 100) => {
    setIsGeneratingBulk(true);
    
    // Types for variety
    const types: LearningCard["type"][] = ["Quiz", "Tip", "Link"];
    
    // Topic variations for content generation
    const topics = [
      "Communication", "Leadership", "Time Management", "Career Planning",
      "Networking", "Interviews", "Resume", "Personal Branding",
      "Conflict Resolution", "Emotional Intelligence", "Public Speaking", "Project Management",
      "Team Work", "Problem Solving", "Critical Thinking", "Negotiation",
      "Work-Life Balance", "Stress Management", "Goal Setting", "Performance Reviews",
      "Remote Work", "Cross-Cultural Communication", "Mentoring", "Feedback"
    ];
    
    const newCards: LearningCard[] = [];
    
    // Generate cards with varied content
    for (let i = 0; i < count; i++) {
      const type = types[Math.floor(Math.random() * types.length)];
      const topic = topics[Math.floor(Math.random() * topics.length)];
      
      let content = "";
      let title = "";
      
      switch (type) {
        case "Quiz":
          title = `${topic} Quiz`;
          content = `What is important for ${topic.toLowerCase()}?\n\na) Practice regularly\nb) Learn from others\n\nAnswer: b`;
          break;
          
        case "Tip":
          title = `${topic} Tips`;
          content = `Practice regularly.\nSeek feedback from others.\nStart with basics.`;
          break;
          
        case "Link":
          title = `${topic} Resources`;
          content = `[${topic} Guide](https://example.com/guide)`;
          break;
      }
      
      // Generate ID in format: day2_00001_tips, day2_00002_quiz, etc.
      const cardsPerDay = 12;
      const existingDays = Math.ceil(savedCards.length / cardsPerDay);
      const dayNumber = existingDays + 1 + Math.floor(i / cardsPerDay);
      const cardNumberInDay = (i % cardsPerDay) + 1;
      const cardNumber = cardNumberInDay.toString().padStart(5, '0');
      const typeForId = type.toLowerCase();
      
      // Modify content based on user description if provided
      if (generationDescription.trim()) {
        content = content + `\n\nNote: ${generationDescription}`;
      }

      // Add random image from latte collection (dynamically loaded)
      const randomImageNum = getRandomLatteImage(availableImages);
      const imageAsset = {
        id: `asset_${Date.now()}_${i}_img`,
        type: "image" as const,
        name: `${topic} Image`,
        url: `/assets/img/latte/${randomImageNum}.png`
      };

      newCards.push({
        id: `day${dayNumber}_${cardNumber}_${typeForId}`,
        type,
        title,
        content,
        assets: [...uploadedAssets, imageAsset], // Include uploaded assets + random image
        status: Math.random() > 0.3 ? "published" : "draft",
        createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString()
      });
    }
    
    // Simulate async generation with timeout
    setTimeout(() => {
      setSavedCards(prev => [...prev, ...newCards]);
      setIsGeneratingBulk(false);
      console.log(`Generated ${newCards.length} cards successfully`);
    }, 1500);
  };

  return (
    <div className="mt-3 h-full w-full">
      {/* Generation Controls */}
        <Card extra="w-full p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-end gap-4 w-full md:w-auto">
              <div>
                <label className="text-sm font-medium text-navy-700 dark:text-white mb-1 block">
                  Number of Cards
                </label>
                <select
                  value={generationCount}
                  onChange={(e) => setGenerationCount(Number(e.target.value))}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white h-10"
                  disabled={isGeneratingBulk}
                >
                  <option value={12}>1 Day (12 Cards)</option>
                  <option value={24}>2 Days (24 Cards)</option>
                  <option value={60}>5 Days (60 Cards)</option>
                  <option value={120}>10 Days (120 Cards)</option>
                  <option value={240}>20 Days (240 Cards)</option>
                  <option value={600}>50 Days (600 Cards)</option>
                </select>
              </div>
              
              <button
                onClick={() => generateBulkCards(generationCount)}
                disabled={isGeneratingBulk || isLoadingImages}
                className={`flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-medium text-white transition duration-200 h-10 ${
                  isGeneratingBulk || isLoadingImages
                    ? "bg-gray-400 cursor-not-allowed" 
                    : "bg-purple-500 hover:bg-purple-600"
                }`}
              >
                <MdAutoAwesome className="h-4 w-4" />
                {isGeneratingBulk 
                  ? `Generating ${generationCount} Cards...` 
                  : isLoadingImages 
                  ? 'Loading Images...' 
                  : `Generate ${generationCount} Cards`}
              </button>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-navy-700 dark:text-white">
                  {Math.ceil(savedCards.length / 12)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Days of Content</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-500">
                  {savedCards.filter(c => c.status === "published").length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Published</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-500">
                  {savedCards.filter(c => c.status === "draft").length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Drafts</p>
              </div>
            </div>
          </div>
          
          {isGeneratingBulk && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                <div className="bg-purple-500 h-2 rounded-full animate-pulse" style={{ width: "60%" }}></div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 text-center">
                AI is generating diverse learning content...
              </p>
            </div>
          )}

          {/* Description and Assets Section */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
            <div className="mb-4">
              <h4 className="text-md font-semibold text-navy-700 dark:text-white">
                Generation Guidelines & Assets
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Provide specific instructions and upload resources to customize your generated content
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Description Input */}
              <div>
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                  Content Guidelines
                </label>
                <textarea
                  value={generationDescription}
                  onChange={(e) => setGenerationDescription(e.target.value)}
                  placeholder="Describe the specific focus, audience, or requirements for your learning modules. For example: 'Focus on remote work challenges for software engineers' or 'Include practical examples for beginner mentors in the healthcare industry'"
                  rows={6}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white resize-none"
                />
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>AI will use these guidelines to customize generated content</span>
                  <span>{generationDescription.length}/500</span>
                </div>
              </div>

              {/* Asset Upload */}
              <div>
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                  Upload Assets
                </label>
                
                {/* Drop Zone */}
                <div
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFileUpload(e.dataTransfer.files);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={(e) => e.preventDefault()}
                  className="relative border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-brand-500 dark:hover:border-brand-400 transition-colors"
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Drop files here or click to browse
                  </p>
                  <p className="text-xs text-gray-500">
                    Supports images, videos, documents (PDF, DOC, TXT)
                  </p>
                </div>

                {/* Uploaded Assets List */}
                {uploadedAssets.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                      Uploaded Assets ({uploadedAssets.length})
                    </p>
                    {uploadedAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="flex items-center justify-between bg-gray-50 dark:bg-navy-800 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {getAssetIcon(asset.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                              {asset.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {asset.size}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeAsset(asset.id)}
                          className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Remove asset"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

      {/* Generated Cards Section */}
      {savedCards.length > 0 ? (
        <div className="mt-8">
          <Manager
            cards={savedCards}
            onCardSelect={(card) => {
              // View-only mode, no editing
              console.log("Viewing card:", card);
            }}
            onCardDelete={(cardId) => {
              setSavedCards(prev => prev.filter(card => card.id !== cardId));
            }}
            onCardDuplicate={(card) => {
              // Extract day number from original card
              const dayMatch = card.id.match(/^day(\d+)_/);
              const dayNumber = dayMatch ? parseInt(dayMatch[1]) : 1;
              
              // Find highest card number in that day
              const cardsInSameDay = savedCards.filter(c => c.id.startsWith(`day${dayNumber}_`));
              const highestCardNumber = Math.max(...cardsInSameDay.map(c => {
                const cardMatch = c.id.match(/^day\d+_(\d+)_/);
                return cardMatch ? parseInt(cardMatch[1]) : 1;
              }), 0);
              
              const newCardNumber = (highestCardNumber + 1).toString().padStart(5, '0');
              const typeForId = card.type.toLowerCase();
              
              const duplicated = {
                ...card,
                id: `day${dayNumber}_${newCardNumber}_${typeForId}`,
                title: `${card.title} (Copy)`,
                status: "draft" as const,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              };
              setSavedCards(prev => [...prev, duplicated]);
            }}
            onCardStatusToggle={(cardId, newStatus) => {
              setSavedCards(prev => 
                prev.map(card => 
                  card.id === cardId 
                    ? { ...card, status: newStatus, updatedAt: new Date().toISOString() }
                    : card
                )
              );
            }}
            onCardUpdate={(cardId, updatedCard) => {
              setSavedCards(prev => 
                prev.map(card => 
                  card.id === cardId 
                    ? { ...card, ...updatedCard }
                    : card
                )
              );
            }}
            onAddDay={(navigateToDay) => {
              const cardsPerDay = 12;
              
              // Calculate the next day number
              const existingDays = Array.from(new Set(savedCards.map(card => {
                const dayMatch = card.id.match(/^day(\d+)_/);
                return dayMatch ? parseInt(dayMatch[1]) : 1;
              })));
              const nextDayNumber = Math.max(...existingDays, 0) + 1;
              
              const newCards: LearningCard[] = [];
              
              // Create 12 new cards for the complete new day
              for (let i = 0; i < cardsPerDay; i++) {
                const cardNumber = (i + 1).toString().padStart(5, '0');
                const types: LearningCard["type"][] = ["Quiz", "Tip", "Link"];
                const type = types[i % types.length];
                const typeForId = type.toLowerCase();
                
                // Add random image from latte collection (dynamically loaded)
                const randomImageNum = getRandomLatteImage(availableImages);
                const imageAsset = {
                  id: `asset_day${nextDayNumber}_${i}_img`,
                  type: "image" as const,
                  name: `Day ${nextDayNumber} ${type} Image`,
                  url: `/assets/img/latte/${randomImageNum}.png`
                };

                const newCard: LearningCard = {
                  id: `day${nextDayNumber}_${cardNumber}_${typeForId}`,
                  type: type,
                  title: `New Day ${type} ${i + 1}`,
                  content: type === "Quiz" 
                    ? "What is the topic?\n\na) Option A\nb) Option B\n\nAnswer: b"
                    : type === "Tip" 
                    ? "Add your tips here.\nTip 1: ...\nTip 2: ..."
                    : "[Resource Guide](https://example.com)",
                  assets: [imageAsset],
                  status: "draft",
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                };
                
                newCards.push(newCard);
              }
              
              setSavedCards(prev => [...prev, ...newCards]);
              
              // Navigate to the newly created day
              setTimeout(() => navigateToDay(nextDayNumber), 0);
            }}
            onRemoveDay={(dayNumber) => {
              setSavedCards(prev => 
                prev.filter(card => {
                  const dayMatch = card.id.match(/^day(\d+)_/);
                  const cardDayNumber = dayMatch ? parseInt(dayMatch[1]) : 1;
                  return cardDayNumber !== dayNumber;
                })
              );
            }}
          />
        </div>
      ) : (
        !isGeneratingBulk && (
          <Card extra="w-full p-12">
            <div className="text-center">
              <div className="mx-auto h-32 w-32 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4 overflow-hidden">
                <img 
                  src="/assets/img/latte/100.png" 
                  alt="Generate cards" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
                No Cards Generated Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Use the AI generator above to create learning modules instantly
              </p>
              <button
                onClick={() => generateBulkCards(generationCount)}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-500 px-6 py-3 text-sm font-medium text-white hover:bg-purple-600 transition duration-200"
              >
                <MdAutoAwesome className="h-5 w-5" />
                Generate Your First Batch
              </button>
            </div>
          </Card>
        )
      )}
    </div>
  );
};

export default ContentCreation;